import {range} from '@benchling/js-utils';
import React from 'react';

import {NucleotideType} from 'biojs/BioConstants';
import {SequenceType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  BULK_EDIT_MODS_OLIGO_MAP_ROW_HEIGHT,
  BulkEditModsOligoMap,
  PatternMapLayer,
} from 'modbio/bulk-edit-mods/BulkEditModsOligoMap';
import {getWidthForBaseCount} from 'modbio/polymer-maps/OligoMap';
import {ColumnAccessorKey} from 'nav/expanded/ListingRendererConstants';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {GREEN, ORANGE} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import * as ScrollableTableConstants from 'ui-library/scrollable/ScrollableTableConstants';
import SortableScrollableTable from 'ui-library/scrollable/SortableScrollableTable';

import {SequenceMapPreviewJustify, BulkEditModsResult} from './BulkEditModsConstants';

export const BASES_COLUMN_EXTRA_PADDING = 70;

const BULK_EDIT_MODS_PREVIEW_TEXT_ROW_HEIGHT = 30;
export interface BulkEditModsPreviewsListingProps {
  templateBasesLength: number;
  targetOligoNames: Array<string>;
  results: Array<BulkEditModsResult>;
  justify: SequenceMapPreviewJustify;
  nucleotideType: NucleotideType;
  submitted: boolean;
  customStartPosition: number | null;
  emptyTableText: string;
  successfulResultTooltipText: string;
  previewColumnTitle: string;
  sequenceType?: SequenceType;
}

interface BulkEditModsPreviewRowSortableData {
  name: string;
}

type BulkEditModsPreviewRowKey = keyof BulkEditModsPreviewRowSortableData;

interface BulkEditModsPreviewRowData extends BulkEditModsPreviewRowSortableData {
  oligoMap: JSX.Element | null;
}

export interface BulkEditModsPreviewRow extends ScrollableTableConstants.ScrollableTableRow {
  data: BulkEditModsPreviewRowData;
}

function getTableScrollLeft(
  justify: SequenceMapPreviewJustify,
  templateBasesLength: number,
  customStartPosition: number | null,
  results: Array<BulkEditModsResult>
): number {
  let result: number = 0;
  if (justify === SequenceMapPreviewJustify.RIGHT) {
    const firstValidRowBases = results.find((result) => result.preview?.bases != null)?.preview?.bases;
    result =
      firstValidRowBases != null
        ? getWidthForBaseCount(firstValidRowBases.length) - getWidthForBaseCount(templateBasesLength)
        : 0;
  } else if (justify === SequenceMapPreviewJustify.CUSTOM) {
    result = getWidthForBaseCount(customStartPosition! - 1);
  }
  return Math.max(result, 0);
}

function getTemplateAppliedRange(
  justify: SequenceMapPreviewJustify,
  targetBasesLength: number,
  templateBasesLength: number,
  customStartPosition: number | null
): Array<number> {
  // Note that range function is end inclusive
  if (justify === SequenceMapPreviewJustify.LEFT) {
    return range(0, templateBasesLength - 1);
  } else if (justify === SequenceMapPreviewJustify.CUSTOM) {
    return range(customStartPosition! - 1, customStartPosition! - 2 + templateBasesLength);
  } else {
    return range(targetBasesLength - templateBasesLength, targetBasesLength - 1);
  }
}

export default function BulkEditModsPreviewsListing({
  templateBasesLength,
  targetOligoNames,
  results,
  justify,
  nucleotideType,
  submitted,
  customStartPosition,
  emptyTableText,
  sequenceType,
  successfulResultTooltipText,
  previewColumnTitle,
}: BulkEditModsPreviewsListingProps): JSX.Element {
  const resultBases = results.map((result) => result.preview?.bases).filter((bases) => bases != null);
  const maxBasesWidth =
    resultBases.length > 0 && sequenceType !== SequenceType.ss
      ? Math.max(...resultBases.map((bases) => getWidthForBaseCount(bases.length)))
      : 0; // Note that the table will just stretch to fill anything less than its minimum width
  const basesColumnWidth = maxBasesWidth + BASES_COLUMN_EXTRA_PADDING;

  const tableScrollLeft = getTableScrollLeft(justify, templateBasesLength, customStartPosition, results);
  const columns: Array<ScrollableTableConstants.ScrollableTableColumn> = [
    {
      accessorKey: ColumnAccessorKey.NAME,
      header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
      isFixed: true,
      isCollapsible: false,
      flexGrow: 1,
      sortKey: SortKey.SORT_NAME_KEY,
      isSortable: true,
    },
    {
      accessorKey: ColumnAccessorKey.OLIGO_MAP,
      header: previewColumnTitle,
      width: basesColumnWidth,
      isCollapsible: false,
      flexGrow: 1,
    },
  ];

  function getRowContentHeight(): number {
    if (sequenceType === SequenceType.ss) {
      return BULK_EDIT_MODS_PREVIEW_TEXT_ROW_HEIGHT;
    } else {
      return BULK_EDIT_MODS_OLIGO_MAP_ROW_HEIGHT;
    }
  }

  function getPreviewRow(result: BulkEditModsResult): JSX.Element {
    if (sequenceType === SequenceType.ss) {
      return <div>{result.preview!.changedPositions.length} nucleotides modified</div>;
    }
    return (
      <BulkEditModsOligoMap
        bases={result.preview!.bases}
        modifiedNucleotides={result.preview!.modifiedNucleotides}
        changedPositions={result.preview!.changedPositions}
        isTemplate={false}
        enabledLayers={[PatternMapLayer.AXIS, PatternMapLayer.BASES, PatternMapLayer.MODS]}
        index={0}
        nucleotideType={nucleotideType}
        templateAppliedPositions={getTemplateAppliedRange(
          justify,
          result.preview!.bases.length,
          templateBasesLength,
          customStartPosition
        )}
      />
    );
  }

  const rows: Array<BulkEditModsPreviewRow> = results.map((result, i) => {
    const icon =
      result.error != null ? (
        <Tooltip text={result.error.message}>
          <BenchlingIcon icon="warning" color={ORANGE} />
        </Tooltip>
      ) : submitted ? (
        <Tooltip text={successfulResultTooltipText}>
          <BenchlingIcon icon="done" color={GREEN} />
        </Tooltip>
      ) : undefined;
    return {
      id: `${i}`,
      icon,
      data: {
        oligoMap: result.preview == null ? null : getPreviewRow(result),
        name: targetOligoNames[i],
      },
    };
  });
  return (
    <SortableScrollableTable
      canFilterColumns={false}
      canResizeColumns={true}
      className="u-flex-grow"
      columns={columns}
      getRowContentHeight={getRowContentHeight}
      isCompact={false}
      initialRows={rows}
      getSortValue={(row: BulkEditModsPreviewRow, sortKey: BulkEditModsPreviewRowKey) => row.data[sortKey]}
      initialSort={[]}
      calcLeftPadding={() => 16}
      tableKey={ScrollableTableConstants.TABLE_KEYS.EXPANDED_BULK_EDIT_MODS_PREVIEW_TABLE}
      emptyTableText={emptyTableText}
      scrollLeft={tableScrollLeft}
      matchParentHeight={true}
    />
  );
}
